/**
 * PersonaGreeting — contextual welcome card at the top of the dashboard.
 *
 * Renders ONLY for personas the existing dashboard headline doesn't
 * already serve well:
 *   - transporter   → "Your dispatch board" + open-bids + active-trips counts
 *   - buyer         → "Your search" + watchlist + new-listings counts
 *
 * Breeders and dog owners already get a great default dashboard
 * (kennel stats + Today/Streaks widgets), so we explicitly skip those
 * personas to avoid double-decorating the page.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TruckIcon as Truck, StorefrontIcon as Storefront, ChartBarIcon as ChartBar, HeartStraightIcon as HeartStraight, ArrowRightIcon as ArrowRight, CakeIcon as Cake, HouseIcon as House, CalendarIcon as Calendar, NotePencilIcon as NotePencil, SparkleIcon as Sparkle, PawPrintIcon as PawPrint } from "@phosphor-icons/react";
import { readPersonaOverride } from "@/components/PersonaSwitcher";

export default function PersonaGreeting() {
  const [ctx, setCtx] = useState(null);
  const [override, setOverride] = useState(() => readPersonaOverride());

  useEffect(() => {
    let cancelled = false;
    api.get("/me/nav-context", { silent: true })
      .then((r) => { if (!cancelled) setCtx(r.data || null); })
      .catch(() => { if (!cancelled) setCtx(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onChange = (e) => setOverride(e?.detail?.persona || null);
    window.addEventListener("persona-override-changed", onChange);
    return () => window.removeEventListener("persona-override-changed", onChange);
  }, []);

  if (!ctx) return null;
  const overrideValid = override && (ctx.available || []).includes(override);
  const persona = overrideValid ? override : ctx.primary;

  if (persona === "transporter") return <TransporterCard />;
  if (persona === "buyer") return <BuyerCard />;
  if (persona === "dog_owner" || persona === "owner") return <OwnerCard />;
  return null;
}

function TransporterCard() {
  const [data, setData] = useState(null);
  // Track approved status explicitly. The dispatch-board card only makes
  // sense for *approved* transporters — for non-approved personas the
  // main Dashboard renders a dedicated TransporterDashboardBody with the
  // application CTA, so we'd otherwise be double-decorating the page
  // with stale "—" tiles that link to /transporter (which 403s).
  const [approved, setApproved] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/me/transporter/application-status", { silent: true })
      .then((r) => {
        if (cancelled) return;
        setApproved(r.data?.state === "approved");
      })
      .catch(() => { if (!cancelled) setApproved(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!approved) return;
    let cancelled = false;
    Promise.all([
      api.get("/me/transporter/dashboard", { silent: true }).catch(() => ({ data: null })),
      api.get("/me/transporter/open-requests", { silent: true }).catch(() => ({ data: { requests: [] } })),
    ]).then(([d, o]) => {
      if (cancelled) return;
      const orders = d.data?.orders_by_status || {};
      const active = [...(orders.booked || []), ...(orders.in_transit || [])];
      setData({
        active_trips: active.length,
        open_bids: (o.data?.requests || []).length,
        trips_completed: d.data?.transporter?.trips_completed || 0,
      });
    });
    return () => { cancelled = true; };
  }, [approved]);

  // Status still resolving, OR user is not an approved transporter:
  // render nothing here. The main Dashboard's TransporterDashboardBody
  // surfaces the apply/pending/needs-info states with much richer UX.
  if (approved !== true) return null;

  return (
    <div
      className="mb-6 rounded-2xl border border-[#E5E5E5] bg-gradient-to-br from-[#FBF7EC] to-white p-5 sm:p-6"
      data-testid="persona-greeting-transporter"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-ink text-gold flex-shrink-0">
            <Truck size={20} weight="duotone" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-1">
              Your dispatch board
            </p>
            <h2 className="font-spectral text-2xl tracking-tight leading-tight">
              {data?.open_bids
                ? `${data.open_bids} bid invitation${data.open_bids === 1 ? "" : "s"} waiting`
                : data?.active_trips
                  ? "Trips in motion — keep them rolling"
                  : "Welcome back. No new bids yet."}
            </h2>
          </div>
        </div>
        <Link to="/transporter" className="hidden sm:inline-block">
          <Button size="sm" className="rounded-full bg-ink hover:bg-crimson text-white h-9 px-4 text-[12px]">
            Open dashboard <ArrowRight size={12} weight="bold" className="ml-1" />
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Tile
          icon={ChartBar}
          label="Open bids"
          value={data?.open_bids ?? "—"}
          tone="crimson"
          to="/transporter?focus=bids"
          testId="trp-tile-bids"
        />
        <Tile
          icon={Truck}
          label="Active trips"
          value={data?.active_trips ?? "—"}
          tone="gold"
          to="/transporter"
          testId="trp-tile-active"
        />
        <Tile
          icon={Truck}
          label="Completed"
          value={data?.trips_completed ?? "—"}
          tone="ink"
          to="/transporter"
          testId="trp-tile-completed"
        />
      </div>
    </div>
  );
}

function BuyerCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/dashboard/stats", { silent: true }).catch(() => ({ data: {} })),
      api.get("/marketplace/listings?sort=newest&limit=1", { silent: true }).catch(() => ({ data: {} })),
    ]).then(([s, m]) => {
      if (cancelled) return;
      setData({
        watching_breeders: (s.data?.watching_breeders || []).length,
        watching_litters: (s.data?.watching_litters || []).length,
        new_listings: m.data?.total ?? null,
      });
    });
    return () => { cancelled = true; };
  }, []);

  const watching = (data?.watching_breeders || 0) + (data?.watching_litters || 0);

  return (
    <div
      className="mb-6 rounded-2xl border border-[#E5E5E5] bg-gradient-to-br from-[#FBF7EC] to-white p-5 sm:p-6"
      data-testid="persona-greeting-buyer"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-ink text-gold flex-shrink-0">
            <Storefront size={20} weight="duotone" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-1">
              Your search
            </p>
            <h2 className="font-spectral text-2xl tracking-tight leading-tight">
              {watching > 0
                ? `${watching} kennel${watching === 1 ? "" : "s"} on your watchlist`
                : "Find your next dog"}
            </h2>
          </div>
        </div>
        <Link to="/marketplace" className="hidden sm:inline-block">
          <Button size="sm" className="rounded-full bg-ink hover:bg-crimson text-white h-9 px-4 text-[12px]">
            Browse marketplace <ArrowRight size={12} weight="bold" className="ml-1" />
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Tile
          icon={HeartStraight}
          label="Watching"
          value={watching || "—"}
          tone="crimson"
          to="/offers/my"
          testId="buyer-tile-watching"
        />
        <Tile
          icon={Storefront}
          label="Listings"
          value={data?.new_listings ?? "—"}
          tone="gold"
          to="/marketplace"
          testId="buyer-tile-listings"
        />
        <Tile
          icon={Truck}
          label="Transport"
          value="Bid"
          tone="ink"
          to="/transport"
          testId="buyer-tile-transport"
        />
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone, to, testId }) {
  const toneCls = {
    crimson: "bg-[#FEE2E2] text-[#C1272D]",
    gold:    "bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]",
    ink:     "bg-ink/5 text-ink",
  }[tone] || "bg-[#FBF7EC] text-gold-dark";

  return (
    <Link
      to={to}
      data-testid={testId}
      className="rounded-xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] p-3 transition-colors flex flex-col items-start gap-1"
    >
      <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${toneCls}`}>
        <Icon size={14} weight="duotone" />
      </span>
      <span className="font-spectral text-2xl tabular-nums leading-none text-ink">
        {value}
      </span>
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B]">
        {label}
      </span>
    </Link>
  );
}


/**
 * Owner card — "I love my dog(s)" persona. Surfaces the at-a-glance
 * signals an owner actually cares about: dogs on file, last Dog Log
 * entry, next upcoming care event, and any birthdays / gotcha days
 * landing in the next 7 days. Each tile deep-links into the relevant
 * subsystem.
 */
function OwnerCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/me/owner-summary", { silent: true })
      .then((r) => { if (!cancelled) setData(r.data || null); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);
  if (!data) return null;

  const fmtRelative = (iso) => {
    if (!iso) return null;
    try {
      const t = new Date(iso);
      const days = Math.floor((Date.now() - t.getTime()) / 86_400_000);
      if (days < 1) return "today";
      if (days === 1) return "yesterday";
      if (days < 7) return `${days} days ago`;
      if (days < 30) return `${Math.floor(days / 7)} wk ago`;
      return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return null; }
  };
  const fmtUpcoming = (iso) => {
    if (!iso) return null;
    try {
      const t = new Date(iso);
      const days = Math.floor((t.getTime() - Date.now()) / 86_400_000);
      if (days <= 0) return "today";
      if (days === 1) return "tomorrow";
      if (days < 7) return `in ${days} days`;
      return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return null; }
  };
  const lastEntry = data.last_log_entry;
  const nextAppt = data.next_appointment;
  const allCelebrations = [
    ...(data.upcoming_birthdays || []).map((b) => ({ ...b, kind: "birthday" })),
    ...(data.upcoming_gotchadays || []).map((b) => ({ ...b, kind: "gotcha" })),
  ].sort((a, b) => (a.days_until || 0) - (b.days_until || 0));

  return (
    <section
      className="mb-8 rounded-2xl border border-[#EAEAEA] bg-white p-5 sm:p-6 shadow-sm"
      data-testid="owner-greeting-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <House size={20} weight="duotone" className="text-crimson" />
        <h2 className="font-spectral text-2xl tracking-tight">Your dogs, at a glance</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Link
          to="/dogs"
          data-testid="owner-card-dogs"
          className="rounded-xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] p-3 transition-colors flex flex-col items-start gap-1"
        >
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]">
            <PawPrint size={14} weight="duotone" />
          </span>
          <span className="font-spectral text-2xl tabular-nums leading-none text-ink">{data.dog_count}</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B]">
            {data.dog_count === 1 ? "Dog on file" : "Dogs on file"}
          </span>
        </Link>

        <Link
          to="/dog-log"
          data-testid="owner-card-log"
          className="rounded-xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] p-3 transition-colors flex flex-col items-start gap-1"
        >
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#FEE2E2] text-[#C1272D]">
            <NotePencil size={14} weight="duotone" />
          </span>
          <span className="font-spectral text-2xl tabular-nums leading-none text-ink">
            {lastEntry ? (fmtRelative(lastEntry.ts) || "—") : "—"}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B]">
            {lastEntry ? "Last log entry" : "Dog Log"}
          </span>
        </Link>

        <Link
          to="/calendar"
          data-testid="owner-card-next-appt"
          className="rounded-xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] p-3 transition-colors flex flex-col items-start gap-1"
        >
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-ink/5 text-ink">
            <Calendar size={14} weight="duotone" />
          </span>
          <span className="font-spectral text-2xl tabular-nums leading-none text-ink">
            {nextAppt ? (fmtUpcoming(nextAppt.starts_at) || "—") : "—"}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B]">
            {nextAppt ? (nextAppt.title || nextAppt.kind || "Next event") : "No events"}
          </span>
        </Link>

        <Link
          to="/feed"
          data-testid="owner-card-celebrate"
          className="rounded-xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] p-3 transition-colors flex flex-col items-start gap-1"
        >
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]">
            <Sparkle size={14} weight="duotone" />
          </span>
          <span className="font-spectral text-2xl tabular-nums leading-none text-ink">
            {allCelebrations.length}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B]">
            Celebrations this week
          </span>
        </Link>
      </div>

      {allCelebrations.length > 0 && (
        <div className="rounded-xl bg-[#FBF7EC] border border-[#E8D9A6] p-4 mb-3" data-testid="owner-celebrations">
          <div className="flex items-center gap-2 mb-2">
            <Cake size={16} weight="duotone" className="text-gold-dark" />
            <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-gold-dark">Upcoming celebrations</p>
          </div>
          <ul className="space-y-1.5">
            {allCelebrations.slice(0, 3).map((c) => (
              <li key={`${c.kind}-${c.dog_id}`} className="flex items-center justify-between gap-2 text-sm">
                <Link to={`/dogs/${c.dog_id}`} className="font-medium text-ink hover:text-crimson truncate">
                  {c.name || "Your dog"}
                </Link>
                <span className="text-[12px] text-[#3F3F3F] whitespace-nowrap">
                  {c.is_today
                    ? (c.kind === "birthday"
                        ? `turns ${c.next_age} today!`
                        : `${c.next_age}-year gotcha day today!`)
                    : (c.kind === "birthday"
                        ? `Birthday in ${c.days_until} ${c.days_until === 1 ? "day" : "days"}`
                        : `Gotcha day in ${c.days_until} ${c.days_until === 1 ? "day" : "days"}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Link to="/dogs/new" data-testid="owner-card-add-dog">
          <Button size="sm" className="rounded-full bg-ink hover:bg-crimson text-white">
            <PawPrint size={13} weight="bold" className="mr-1" /> Add a dog
          </Button>
        </Link>
        <Link to="/dog-log" data-testid="owner-card-log-cta">
          <Button size="sm" variant="outline" className="rounded-full">
            Open Dog Log <ArrowRight size={12} weight="bold" className="ml-1" />
          </Button>
        </Link>
        <Link to="/calendar" data-testid="owner-card-calendar">
          <Button size="sm" variant="outline" className="rounded-full">
            Calendar
          </Button>
        </Link>
      </div>
    </section>
  );
}
